import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useOdds from '../../hooks/useOdds';
import OddsTable from './OddsTable';


const ALL_MARKETS = [
  { key: 'spreads', label: 'Spread', tip: 'spread' },
  { key: 'h2h', label: 'ML', tip: 'moneyline' },
  { key: 'totals', label: 'Total', tip: 'overunder' },
];

const MONEYLINE_ONLY_SPORTS = ['mma_mixed_martial_arts', 'boxing_boxing'];

export default function OddsBoard({ sport, oddsFormat, favoriteTeams = [] }) {
  const isMoneylineOnly = MONEYLINE_ONLY_SPORTS.includes(sport);
  const markets = useMemo(
    () => isMoneylineOnly ? ALL_MARKETS.filter((m) => m.key === 'h2h') : ALL_MARKETS,
    [isMoneylineOnly],
  );

  const [market, setMarket] = useState(isMoneylineOnly ? 'h2h' : 'spreads');
  const { events, loading, error, refetch } = useOdds(sport);
  const navigate = useNavigate();

  useEffect(() => {
    setMarket(isMoneylineOnly ? 'h2h' : 'spreads');
  }, [sport, isMoneylineOnly]);

  return (
    <div className="px-5 py-4">
      {/* Controls */}
      <div className="flex items-center justify-between mb-3 relative z-20">
        <div className="inline-flex bg-[#E2E4E9] dark:bg-[#1a2030] rounded-lg p-1 gap-0.5">
          {markets.map((m) => (
            <button
              key={m.key}
              onClick={() => setMarket(m.key)}
              className={`px-6 py-2 rounded-md text-[15px] transition-all duration-150 ${
                market === m.key
                  ? 'bg-white dark:bg-[#1e2a3a] text-[#005BB3] dark:text-[#3b82f6] font-bold shadow-sm'
                  : 'text-[#6B7280] dark:text-[#8892a8] font-semibold hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
              }`}
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              {m.label}
            </button>
          ))}
        </div>

        <button
          onClick={refetch}
          disabled={loading}
          className="p-1.5 rounded text-[#64748b] dark:text-[#505a6e] hover:text-[#1e293b] dark:hover:text-[#e2e8f0] hover:bg-[#f1f5f9] dark:hover:bg-[#151a25] transition-colors duration-150 disabled:opacity-40"
        >
          <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-3">
          <p className="text-[#ef4444] text-[11px]">Failed to load odds: {error}</p>
          <button onClick={refetch} className="text-[10px] text-[#ef4444] underline mt-1">Retry</button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="space-y-px">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-11 bg-[#f1f5f9] dark:bg-[#151a25] skeleton" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#8892a8] text-sm">No events found.</p>
          <p className="text-[#505a6e] text-[11px] mt-1">Check back during the season.</p>
        </div>
      ) : (
        <OddsTable
          events={events}
          market={market}
          oddsFormat={oddsFormat}
          onEventClick={(eventId) => navigate(`/game/${eventId}?sport=${sport}`)}
          favoriteTeams={favoriteTeams}
        />
      )}
    </div>
  );
}
